import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, Input, Select, Button, Table, Space, Tag, Badge, message, Modal, Popover, PaginationProps, Descriptions, Alert, Divider } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { whatsappConnectorAgentApi, AgentMessageQuery } from '../services/whatsappConnectorAgentApi';

const { Option } = Select;
const defaultPageSize = 20;

const AgentMessagesPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  const queryParams: AgentMessageQuery = useMemo(() => {
    const values = form.getFieldsValue();
    return {
      page,
      pageSize,
      conversation_id: values.conversation_id,
      direction: values.direction,
      status: values.status
    };
  }, [form, page, pageSize]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await whatsappConnectorAgentApi.getAgentMessages(queryParams);
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (error: any) {
      message.error('加载消息失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const onSearch = () => {
    setPage(1);
    loadData();
  };

  const onReset = () => {
    form.resetFields();
    setPage(1);
    setPageSize(defaultPageSize);
    loadData();
  };

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailText, setDetailText] = useState('');
  const [detailRecord, setDetailRecord] = useState<any>(null);

  const extractMessageText = (r: any): string => {
    let raw: any = r?.content ?? r?.messages ?? r?.message;
    const tryParse = (s: string) => { try { return JSON.parse(s); } catch { return s; } };
    if (typeof raw === 'string') raw = tryParse(raw);
    if (raw && typeof raw === 'object' && 'messages' in raw) {
      let msgs: any = (raw as any).messages;
      if (typeof msgs === 'string') msgs = tryParse(msgs);
      if (Array.isArray(msgs)) return msgs.map((it: any) => it?.content ?? it?.text ?? JSON.stringify(it)).join('\n\n');
    }
    if (Array.isArray(raw)) return raw.map((it: any) => it?.content ?? it?.text ?? JSON.stringify(it)).join('\n\n');
    if (raw && typeof raw === 'object') {
      if ('content' in raw && (raw as any).content) return (raw as any).content as string;
      return JSON.stringify(raw, null, 2);
    }
    if (typeof raw === 'string') return raw.replace(/\\n/g, '\n');
    return '-';
  };

  const formatMaybeJson = (input: any): string => {
    if (input == null) return '-';
    if (typeof input === 'string') {
      try {
        const parsed = JSON.parse(input);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return input.replace(/\\n/g, '\n');
      }
    }
    if (typeof input === 'object') {
      return JSON.stringify(input, null, 2);
    }
    return String(input);
  };

  const columns = [
    { title: '消息ID', key: 'message_id', width: 160, ellipsis: true, render: (_: any, r: any) => r.message_id || r.messageId || r.id || '-' },
    { title: '会话ID', key: 'conversation_id', width: 200, ellipsis: true, render: (_: any, r: any) => r.conversation_id || r.conversationId || '-' },
    { title: '方向', dataIndex: 'direction', key: 'direction', width: 100, render: (v: string, r: any) => <Tag color={(v || r.direction) === 'outbound' ? 'green' : 'geekblue'}>{v || r.direction}</Tag> },
    { title: '状态', key: 'status', width: 120, render: (_: any, r: any) => {
      const v = r.status;
      return <Badge status={v === 'sent' ? 'success' : v === 'failed' ? 'error' : 'processing'} text={v || '-'} />;
    } },
    { title: '内容', key: 'content', ellipsis: true, render: (_: any, r: any) => {
      const s = extractMessageText(r);
      const preview = s.length > 120 ? s.slice(0, 120) + '…' : s;
      return (
        <Popover
          placement="topLeft"
          overlayStyle={{ maxWidth: 800 }}
          content={<pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{s}</pre>}
        >
          <span style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}>{preview}</span>
        </Popover>
      );
    } },
    { title: '时间', key: 'created_at', width: 180, render: (_: any, r: any) => r.created_at || r.createdAt || '-' },
    { title: '操作', key: 'action', width: 90, render: (_: any, r: any) => (
      <Button type="link" onClick={() => { setDetailRecord(r); setDetailText(extractMessageText(r)); setDetailOpen(true); }}>查看</Button>
    ) }
  ];

  const onTableChange: PaginationProps['onChange'] = (p, ps) => {
    setPage(p);
    setPageSize(ps || defaultPageSize);
  };

  return (
    <Card title="Agent 消息" extra={<Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>}>
      <Form form={form} layout="vertical" onFinish={onSearch}>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="conversation_id" label="会话ID">
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="direction" label="方向">
              <Select allowClear placeholder="请选择">
                <Option value="inbound">inbound</Option>
                <Option value="outbound">outbound</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="status" label="状态">
              <Input allowClear placeholder="如 sent, failed, processing" />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onReset}>重置</Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
          </Col>
        </Row>
      </Form>

      <Table
        style={{ marginTop: 16 }}
        rowKey={(r: any) => String(r.id || r.message_id || r.messageId)}
        loading={loading}
        columns={columns as any}
        dataSource={rows}
        pagination={{ current: page, pageSize, total, showSizeChanger: true, showQuickJumper: true }}
        onChange={(pagination) => onTableChange(pagination.current!, pagination.pageSize || defaultPageSize)}
      />

      <Modal
        title={`Agent 消息详情 - ${detailRecord?.direction === 'request' ? '请求' : '响应'}`}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={<Button type="primary" onClick={() => setDetailOpen(false)}>关闭</Button>}
        width={900}
        style={{ top: 20 }}
      >
        {detailRecord && (
          <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            {/* 基本信息 */}
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="消息ID">
                {detailRecord.message_id || detailRecord.messageId || detailRecord.id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="请求ID">
                {detailRecord.request_id || detailRecord.requestId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="会话ID" span={2}>
                {detailRecord.conversation_id || detailRecord.conversationId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="方向">
                <Tag color={detailRecord.direction === 'request' ? 'geekblue' : 'green'}>
                  {detailRecord.direction === 'request' ? '请求' : '响应'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Badge 
                  status={detailRecord.status === 'success' ? 'success' : detailRecord.status === 'failed' ? 'error' : 'processing'} 
                  text={detailRecord.status || '-'} 
                />
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {detailRecord.created_at || detailRecord.createdAt || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {detailRecord.updated_at || detailRecord.updatedAt || '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* 错误信息 */}
            {(detailRecord.error_reason || detailRecord.errorReason) && (
              <Alert 
                message="错误信息" 
                description={detailRecord.error_reason || detailRecord.errorReason} 
                type="error" 
                showIcon 
                style={{ marginBottom: 16 }}
              />
            )}

            {/* 消息内容 */}
            <Divider orientation="left" style={{ margin: '16px 0' }}>消息内容</Divider>
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word', 
                margin: 0,
                maxHeight: '200px',
                overflowY: 'auto',
                fontSize: '12px'
              }}>
                {detailText}
              </pre>
            </Card>

            {/* Messages 数据 */}
            {detailRecord.messages && (
              <>
                <Divider orientation="left" style={{ margin: '16px 0' }}>Messages 数据</Divider>
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f5ff' }}>
                  <pre style={{ 
                    whiteSpace: 'pre-wrap', 
                    wordBreak: 'break-word', 
                    margin: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    fontSize: '12px'
                  }}>
                    {formatMaybeJson(detailRecord.messages)}
                  </pre>
                </Card>
              </>
            )}

            {/* Files 数据 */}
            {detailRecord.files && detailRecord.files !== 'null' && detailRecord.files !== '[]' && (
              <>
                <Divider orientation="left" style={{ margin: '16px 0' }}>Files 数据</Divider>
                <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}>
                  <pre style={{ 
                    whiteSpace: 'pre-wrap', 
                    wordBreak: 'break-word', 
                    margin: 0,
                    maxHeight: '150px',
                    overflowY: 'auto',
                    fontSize: '12px'
                  }}>
                    {formatMaybeJson(detailRecord.files)}
                  </pre>
                </Card>
              </>
            )}

            {/* Payload 数据 */}
            <Divider orientation="left" style={{ margin: '16px 0' }}>Payload 数据</Divider>
            <Card size="small" style={{ marginBottom: 0, backgroundColor: '#fff7e6' }}>
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word', 
                margin: 0,
                maxHeight: '300px',
                overflowY: 'auto',
                fontSize: '12px'
              }}>
                {formatMaybeJson(detailRecord.payload)}
              </pre>
            </Card>
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default AgentMessagesPage;


